import * as monaco from 'monaco-editor'
import { debounce } from 'lodash'

var editor: monaco.editor.IStandaloneCodeEditor

const ENABLE_WEBDAV = process.env.ENABLE_WEBDAV

declare global {
  interface Window {
    MonacoEnvironment?: monaco.Environment
    useWebDav: boolean
    path: string
  }
}

window.MonacoEnvironment = {
  getWorkerUrl: function () {
    return './editor.worker.bundle.js'
  },
}

const setLastCode = async (code: string) => {
  if (ENABLE_WEBDAV) {
    try {
      const response = await fetch(window.path, { method: 'PUT', body: code })
      if (!response.ok) {
        throw new Error(`Response status: ${response.status}`)
      }
    } catch (error) {
      console.error(error)
    }
  } else if (window.localStorage) {
    window.localStorage.setItem('monaco-editor-lastcode', code)
  }
}

const getLastCode = async () => {
  if (ENABLE_WEBDAV) {
    const propfindResponse = await fetch(window.path, { method: 'PROPFIND' })
    if (propfindResponse.status !== 207) {
      return undefined
    }
    try {
      const response = await fetch(window.path)
      if (!response.ok) {
        throw new Error(`Response status: ${response.status}`)
      }
      return await response.text()
    } catch (error) {
      console.error(error)
    }
  } else if (window.localStorage && window.localStorage.getItem('monaco-editor-lastcode')) {
    return window.localStorage.getItem('monaco-editor-lastcode')
  }

  return ''
}

export const getCode = () => {
  return editor.getValue()
}

export const setFocusToMonacoEditor = () => {
  editor.focus()
}

export async function initializeMonacoEditor(codeEditorId: string) {
  const initialCode = await getLastCode()
  const editorContainer = document.getElementById(codeEditorId)
  if (!editorContainer) {
    return
  }

  const saveIndicator = document.getElementById('save-indicator')

  editor = monaco.editor.create(document.getElementById(codeEditorId), {
    automaticLayout: true,
    language: 'python',
    lineNumbersMinChars: 2,
    minimap: {
      enabled: false,
    },
    scrollBeyondLastLine: false,
    tabSize: 4,
    renderWhitespace: 'all',
    value: initialCode,
    wordWrap: 'on',
  })

  // overwrite some keybindings
  editor.addCommand(monaco.KeyMod.Alt | monaco.KeyCode.KeyR, () => {})
  editor.addCommand(monaco.KeyMod.Alt | monaco.KeyCode.KeyC, () => {})
  editor.addCommand(monaco.KeyMod.Alt | monaco.KeyCode.KeyW, () => {})

  if (ENABLE_WEBDAV) {
    // Auto save
    editor.onDidChangeModelContent(() => {
      saveIndicator.textContent = '...'
      debouncedSave(editor.getValue())
    })
  } else {
    document.getElementById(codeEditorId).addEventListener('keyup', () => {
      setLastCode(editor.getValue())
    })
  }

  // Auto save with 1 second debounce
  const debouncedSave = debounce((value: string) => {
    setLastCode(value)
    saveIndicator.textContent = '✓ Tallennettu'
  }, 1000)

  document
    .getElementById('tab-programming-ide-container')
    .addEventListener('replaceEditorTextForTestingPurposes', (event: CustomEvent) => {
      editor.setValue(event.detail.text)
    })
}
